import { uniqueId } from 'lodash';

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { useAppDispatch } from '../../hooks/useAppDispatcher.hook';
import { useModalManager } from '../../hooks/ModalManager.hook';
import { setSelectedPersonLinkAction } from '../../redux/Candidate/Link/PersonLink.slice';
import { PersonLinkEditState } from '../../redux/Candidate/Link/PersonLink.type';

import PersonLinkEditModal from '../Modal/Candidate/Link/PersonLinkEditModal';
import ButtonComponent from '../Button.component';

import CatHelloIcon from '../Icons/Cat/CatHello.icon';
import { StyledBaseIcon } from '../Icons/Base.icon';
import { PERSON_LINKS_ICONS } from '../Icons/PersonLinks.icons';
import ChevronRightIcon from '../Icons/ChevronRight.icon';

import { DATE_TIME_FORMAT } from '../../constants/date.contant';
import TooltipComponent from '../Tooltip.component';
import { StyledTooltipWrapper } from '../../styles';

export interface PersonEditModalLinksProps {
  value: PersonLinkEditState[];
  onChangeLinks: Function;
}

const MAX_LINKS = 20;

const StyledEmptyPersonLinkTitle = styled.div`
  display: flex;
  text-align: center;
  justify-content: center;
  padding: 20px;
  font-size: 20px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.LABEL};
`;
const StyledEmptyPersonLink = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  width: 100%;
  height: 100%;

  & > ${StyledBaseIcon} {
    opacity: 0.1;
  }
`;
const StyledPersonLinkItemLink = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 165px;
  overflow: hidden;
`;
const StyledPersonLinkItemDetailItem = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  font-size: 12px;
`;
const StyledPersonLinkItemDetailItemWait = styled(
  StyledPersonLinkItemDetailItem,
)`
  color: ${({ theme }) => theme.colors.ORANGE.O400};
`;
const StyledPersonLinkItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 12px;
`;
const StyledPersonLinkItemType = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const StyledPersonLinkFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.BORDER};
  padding: 10px;
`;
const StyledPersonLinkHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.BORDER};
  font-weight: 500;
  font-size: 16px;
  line-height: 32px;
`;
const StyledPersonLinkItem = styled.div`
  display: grid;
  grid-template-columns: 42px 1fr 20px;
  align-items: center;
  padding: 5px 0;
  margin: 0 10px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.LABEL};
  transition: all 0.3s;
  border: 1px solid transparent;
  border-radius: 6px;
  min-height: 3rem;

  :hover {
    background: ${({ theme }) => theme.colors.BLUE.B50};
    color: ${({ theme }) => theme.colors.BLUE.B400};
    border-color: ${({ theme }) => theme.colors.BLUE.B400};
  }
`;
const StyledPersonLinkContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: flex-start;
  overflow: auto;
  flex: 1 1;
  height: 100%;
`;
const StyledEditModalPersonLinkWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
  height: 100%;
`;
const StyledEditModalPersonLinkContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100vh;
`;

const PersonEditModalLinksBlockComponent = ({
  value: _links,
  onChangeLinks,
}: PersonEditModalLinksProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { openModal } = useModalManager();
  const [links, setLinks] = useState(_links);

  useEffect(() => {
    setLinks(
      _links?.map((link: PersonLinkEditState) => ({
        ...link,
        tmpUUID: link.uuid || link.tmpUUID,
      })),
    );
  }, [_links]);

  const handleOpenPersonLinkEdit = (link?: PersonLinkEditState) => {
    dispatch(setSelectedPersonLinkAction(link));
    openModal(
      PersonLinkEditModal,
      {
        handleChange: handlePersonLinkChange,
      },
      {
        onClose: () => dispatch(setSelectedPersonLinkAction(undefined)),
      },
    );
  };

  const handlePersonLinkChange = (form: any) => {
    const isAlready = links.find((link: PersonLinkEditState) =>
      form.tmpUUID ? form.tmpUUID === link.tmpUUID : false,
    );
    let newLinks = [...links];
    if (isAlready) {
      newLinks = newLinks
        .filter((link: PersonLinkEditState) => {
          if (link.tmpUUID === isAlready.tmpUUID) {
            return !(form.isDeleted && !link.uuid);
          }

          return true;
        })
        .map((link: PersonLinkEditState) => {
          if (link.tmpUUID === isAlready.tmpUUID) {
            return {
              ...link,
              link: form.link,
              linkRaw: form.link,
              type: form.type.value,
              isChanged: true,
              isDeleted: form.isDeleted,
            };
          }

          return link;
        });
    } else {
      newLinks = [
        ...newLinks,
        {
          ...form,
          type: form.type.value,
          isChanged: true,
        },
      ];
    }

    onChangeLinks(newLinks);
  };

  return (
    <StyledEditModalPersonLinkContainer>
      {links?.length > 0 ? (
        <StyledEditModalPersonLinkWrapper>
          <StyledPersonLinkHeader>{t('External links')}</StyledPersonLinkHeader>
          <StyledPersonLinkContent>
            {links?.map((link: PersonLinkEditState) => (
              <StyledPersonLinkItem
                key={uniqueId('link-')}
                onClick={() => handleOpenPersonLinkEdit(link)}
              >
                <StyledPersonLinkItemType>
                  {PERSON_LINKS_ICONS[link.type]}
                </StyledPersonLinkItemType>
                <StyledPersonLinkItemDetails>
                  <StyledPersonLinkItemDetailItem>
                    <span>{t('Type')}:</span>
                    <strong>{t(link.type)}</strong>
                  </StyledPersonLinkItemDetailItem>
                  <StyledPersonLinkItemDetailItem>
                    <span>{t('Url')}:</span>
                    <StyledPersonLinkItemLink>
                      {link.link || link.linkRaw}
                    </StyledPersonLinkItemLink>
                  </StyledPersonLinkItemDetailItem>
                  {(link.updatedAt || link.createdAt) && (
                    <StyledPersonLinkItemDetailItem>
                      <span>{t('Date')}:</span>
                      <strong>
                        {dayjs(link.updatedAt || link.createdAt).format(
                          DATE_TIME_FORMAT,
                        )}
                      </strong>
                    </StyledPersonLinkItemDetailItem>
                  )}
                  {link.isChanged && !link.isDeleted && (
                    <StyledPersonLinkItemDetailItemWait>
                      {t('Waiting save')}
                    </StyledPersonLinkItemDetailItemWait>
                  )}
                  {link.isChanged && link.isDeleted && (
                    <StyledPersonLinkItemDetailItemWait>
                      {t('Waiting deletion')}
                    </StyledPersonLinkItemDetailItemWait>
                  )}
                </StyledPersonLinkItemDetails>
                <ChevronRightIcon />
              </StyledPersonLinkItem>
            ))}
          </StyledPersonLinkContent>
          <StyledPersonLinkFooter>
            <StyledTooltipWrapper>
              <ButtonComponent
                disabled={links?.length >= MAX_LINKS}
                handleClick={() => handleOpenPersonLinkEdit()}
              >
                {t('Add external link')}
              </ButtonComponent>
              {links?.length >= MAX_LINKS && (
                <TooltipComponent
                  tooltip={t('Maximum number of links:', { max: MAX_LINKS })}
                />
              )}
            </StyledTooltipWrapper>
          </StyledPersonLinkFooter>
        </StyledEditModalPersonLinkWrapper>
      ) : (
        <StyledEmptyPersonLink>
          <StyledEmptyPersonLinkTitle>
            {t('personLink.modalTitle')}
          </StyledEmptyPersonLinkTitle>
          <CatHelloIcon />
          <ButtonComponent handleClick={() => handleOpenPersonLinkEdit()}>
            {t('Add external link')}
          </ButtonComponent>
        </StyledEmptyPersonLink>
      )}
    </StyledEditModalPersonLinkContainer>
  );
};

export default PersonEditModalLinksBlockComponent;
